import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { Flex, hasTextContent, Icon, type SvgIcon } from '@bridebook/aura';
import { cn } from '../../lib/utils';
import { Loader } from '@bridebook/aura/src/icons';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import {
  buttonDisabledClasses,
  buttonFocusClasses,
  buttonIconVariants,
  buttonLayoutClasses,
  buttonSizeConfig,
  buttonSpinnerSizeConfig,
  iconSizeMap,
  type RegularButtonSize,
} from './button-shared';
import { ButtonAiSecondary } from './ai-button/button-ai-secondary';

const buttonVariants = cva(
  cn(buttonLayoutClasses, 'border', buttonFocusClasses, buttonDisabledClasses),
  {
    variants: {
      // `data-disabled:` repeats the disabled look for Base UI parts (e.g. menu inside PopoverMenu), which
      // keep a disabled button focusable and set data-disabled instead of the native attribute
      variant: {
        primary:
          'border-indigo-crush bg-indigo-crush font-semibold text-white hover:border-indigo-crush-80 hover:bg-indigo-crush-80 active:border-indigo-crush-120 active:bg-indigo-crush-120 disabled:border-indigo-crush-40 disabled:bg-indigo-crush-40 disabled:text-space-04 data-disabled:border-indigo-crush-40 data-disabled:bg-indigo-crush-40 data-disabled:text-space-04',
        secondary:
          'border-indigo-crush bg-white font-semibold text-indigo-crush hover:bg-indigo-crush-10 active:bg-indigo-crush-20 disabled:border-indigo-crush-40 disabled:text-indigo-crush/40 data-disabled:border-indigo-crush-40 data-disabled:text-indigo-crush/40',
        tertiary:
          'border-space-15 bg-white font-normal text-space hover:bg-space-04 active:bg-space-08 disabled:text-space/30 data-disabled:text-space/30',
        'tertiary-b2b':
          'border-space-04 bg-space-04 font-normal text-space hover:border-space-08 hover:bg-space-08 active:border-space-15 active:bg-space-15 disabled:border-space-04 disabled:bg-space-04 disabled:text-space/40 data-disabled:border-space-04 data-disabled:bg-space-04 data-disabled:text-space/40',
        ghost:
          'border-transparent bg-transparent font-semibold text-indigo-crush hover:bg-indigo-crush-10 active:bg-indigo-crush-20 disabled:text-indigo-crush/40 data-disabled:text-indigo-crush/40',
        destructive:
          'border-errors bg-white font-semibold text-errors hover:bg-pink-joy-60 active:bg-blush-tangerine-40 disabled:border-blush-tangerine disabled:text-errors/40 data-disabled:border-blush-tangerine data-disabled:text-errors/40',
        'ai-primary': 'button--ai-primary border-transparent font-semibold text-white',
        'ai-secondary': '',
      },
      size: buttonSizeConfig,
      loading: {
        true: 'text-transparent disabled:text-transparent',
      },
    },
  },
);

const loadingIconVariants = cva('animate-spin', {
  variants: {
    variant: {
      primary: 'text-space-04',
      secondary: 'text-indigo-crush/40',
      tertiary: 'text-space/30',
      'tertiary-b2b': 'text-space/40',
      ghost: 'text-indigo-crush/40',
      destructive: 'text-errors/40',
      'ai-primary': 'text-white/40',
    },
    size: buttonSpinnerSizeConfig,
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Use the button as a child element (renders as Slot component from Radix).
   * Useful for creating button-styled links or other elements.
   */
  asChild?: boolean;
  children?: React.ReactNode;
  /**
   * Icon to display on the left side of the button (with text) or alone (icon only button).
   */
  icon?: SvgIcon;
  /**
   * Icon to display on the right side of the button text.
   */
  iconRight?: SvgIcon;
  /**
   * Show loading spinner and disable the button during loading state.
   */
  loading?: boolean;
  /**
   * Button size
   */
  size?: RegularButtonSize;
}

export { buttonIconVariants, iconSizeMap, type RegularButtonSize } from './button-shared';

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      loading = false,
      children,
      disabled,
      variant = 'primary',
      size = 'standard',
      asChild = false,
      icon,
      iconRight,
      ...props
    },
    ref,
  ) => {
    if (variant === 'ai-secondary') {
      const secondaryProps = {
        ref,
        className,
        loading,
        disabled,
        size,
        asChild,
        icon,
        iconRight,
        ...props,
      };
      return <ButtonAiSecondary {...secondaryProps}>{children}</ButtonAiSecondary>;
    }

    const Comp = asChild ? Slot : 'button';

    const isIconOnly = !hasTextContent(children) && icon && !iconRight;
    const sizeToUse = isIconOnly ? iconSizeMap[size] : size;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size: sizeToUse, className, loading }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}>
        {icon && <Icon icon={icon} className={buttonIconVariants({ size: sizeToUse })} />}
        <Slottable>{children}</Slottable>
        {loading && (
          <Flex className="absolute inset-0 w-full items-center justify-center">
            <Icon icon={Loader} className={loadingIconVariants({ variant, size: sizeToUse })} />
          </Flex>
        )}
        {iconRight && <Icon icon={iconRight} className={buttonIconVariants({ size: sizeToUse })} />}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
